import { motion} from "framer-motion";
import { useRef, useEffect, useState } from "react";
import {
  FaArrowRight,
  FaRegLightbulb, FaCode,
  FaRocket, FaHeadset, FaCheck, FaPlay, FaPause,
  FaArrowDown, FaInfinity, FaBolt, FaMagnifyingGlass
} from "react-icons/fa6";
import { FaShieldAlt } from "react-icons/fa";
import { HiOutlineCog } from "react-icons/hi";
import "./process.css";

/* ─── PROCESS DATA ─────────────────────────────────────── */
const phases = [
  {
    id: 1,
    title: "Discovery",
    icon: FaRegLightbulb,
    color: "#FFB347",
    description: "We extract the essence of your vision through deep-dive workshops and competitive analysis.",
    activities: ["Vision Workshop", "Market Research", "Technical Feasibility", "Success Metrics"],
    duration: "1-2 weeks"
  },
  {
    id: 2,
    title: "Architecture",
    icon: HiOutlineCog,
    color: "#4A90E2",
    description: "Blueprints that scale. We design systems with security, performance, and future growth in mind.",
    activities: ["System Design", "Tech Stack Selection", "Security Architecture", "Data Modeling"],
    duration: "2-3 weeks"
  },
  {
    id: 3,
    title: "Build",
    icon: FaCode,
    color: "#12AF09",
    description: "Agile development with daily builds, continuous integration, and zero compromise on quality.",
    activities: ["Sprint Planning", "Daily Builds", "Code Reviews", "Integration Tests"],
    duration: "8-16 weeks"
  },
  {
    id: 4,
    title: "Audit",
    icon: FaShieldAlt,
    color: "#E54B4B",
    description: "Multi-layered security: automated scans, manual review, and third-party verification.",
    activities: ["Automated Testing", "Manual Audit", "Penetration Testing", "Bug Bounty Prep"],
    duration: "2-4 weeks"
  },
  {
    id: 5,
    title: "Launch",
    icon: FaRocket,
    color: "#9B59B6",
    description: "Orchestrated deployment with monitoring, analytics, and war-room support.",
    activities: ["Deployment Strategy", "Load Testing", "Monitoring Setup", "Go/No-Go"],
    duration: "1 week"
  },
  {
    id: 6,
    title: "Evolve",
    icon: FaInfinity,
    color: "#F39C12",
    description: "Post-launch partnership. We optimize, update, and scale as you grow.",
    activities: ["Performance Monitoring", "Feature Updates", "24/7 Support", "Growth Consulting"],
    duration: "Ongoing"
  }
];

const principles = [
  {
    icon: FaBolt,
    title: "Speed without chaos",
    desc: "We move fast, but never at the expense of stability or security."
  },
  {
    icon: FaMagnifyingGlass,
    title: "Radical transparency",
    desc: "You see everything — our backlog, our progress, our metrics."
  },
  {
    icon: FaShieldAlt,
    title: "Security baked in",
    desc: "Not an afterthought. Every line of code is reviewed, tested, hardened."
  },
  {
    icon: FaHeadset,
    title: "Partnership, not vendor",
    desc: "We're invested in your success, long after the launch party."
  }
];

const metrics = [
  { value: "950+", label: "Projects", sub: "delivered on time" },
  { value: "0", label: "Critical failures", sub: "in production" },
  { value: "4.2x", label: "Faster", sub: "than industry average" },
  { value: "100%", label: "Retention", sub: "of enterprise clients" }
];


/* ─── PAGE ─────────────────────────────────────────────── */
export default function ProcessPage() {
  const [activePhase, setActivePhase] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotate phases
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setActivePhase((prev) => (prev % 6) + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div className="process-page" ref={containerRef}>

      {/* ═══ HERO - Minimal, bold ═══════════════════════════ */}
      <section className="process-hero">
        <div className="process-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="process-hero-tagline"
          >
            <span className="hero-line"></span>
            <span>BUILT FOR BUILDERS</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="process-hero-title"
          >
          From Idea to <span>Successful Product</span><br />
          with a Proven Process
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="process-hero-description"
          >
            Six phases. One obsession: turning your complex requirements into 
            production-ready systems that don't break, don't bend, and don't compromise.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="process-hero-actions"
          >
            <a href="#phases" className="btn-primary">Explore the process <FaArrowDown /></a>
            <a href="#contact" className="btn-outline">Start a project</a>
          </motion.div>
        </div>

        <motion.div 
          className="process-hero-visual"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          style={{
            display: 'block',
          }}
        >
          <style jsx>{`
            @media (max-width: 768px) {
              .process-hero-visual {
                display: none !important;
              }
            }
          `}</style>
          <div className="floating-card card-1">
            <FaCode /> Smart contracts
          </div>
          <div className="floating-card card-2">
            <FaShieldAlt /> Audited
          </div>
          <div className="floating-card card-3">
            <FaRocket /> Live in 12 weeks
          </div>
          <div className="hero-grid">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="grid-dot"></div>
            ))}
          </div>
        </motion.div>

        <div className="hero-scroll">
          <span>Scroll to explore</span>
          <FaArrowDown />
        </div>
      </section>

      {/* ═══ METRICS STRIP ═════════════════════════════════ */}
      <section className="metrics-strip">
        <div className="metrics-container">
          {metrics.map((metric, i) => (
            <motion.div
              key={i}
              className="metric-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="metric-value">{metric.value}</div>
              <div className="metric-label">{metric.label}</div>
              <div className="metric-sub">{metric.sub}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ═══ INTERACTIVE PHASE EXPLORER ═══════════════════ */}
      <section id="phases" className="phase-explorer">
        <div className="phase-header">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="phase-tag"
          >
            <span className="tag-line"></span>
            <span>THE BLUEPRINT</span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="phase-title"
          >
            From concept to <span>production</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="phase-subtitle"
          >
            Every project follows our proven path. No shortcuts, no surprises.
          </motion.p>
        </div>

        {/* Interactive phase selector */}
        <div className="phase-selector">
          <div className="phase-numbers">
            {phases.map((phase) => (
              <button
                key={phase.id}
                className={`phase-number-btn ${activePhase === phase.id ? 'active' : ''}`}
                onClick={() => setActivePhase(phase.id)}
                style={{ '--phase-color': phase.color }}
              >
                0{phase.id}
              </button>
            ))}
          </div>

          <button 
            className="phase-play-btn"
            onClick={() => setIsPlaying(!isPlaying)}
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
            <span>{isPlaying ? 'Pause' : 'Play'}</span>
          </button>
        </div>

        {/* Active phase display */}
        <div className="active-phase-display">
          {phases.map((phase) => (
            <motion.div
              key={phase.id}
              className={`phase-card ${activePhase === phase.id ? 'active' : ''}`}
              initial={{ opacity: 0, x: 50 }}
              animate={activePhase === phase.id ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.5 }}
              style={{ '--phase-accent': phase.color }}
            >
              <div className="phase-card-left">
                <div className="phase-icon" style={{ background: `${phase.color}20` }}>
                  <phase.icon style={{ color: phase.color }} />
                </div>
                <div className="phase-meta">
                  <span className="phase-number-display">Phase 0{phase.id}</span>
                  <span className="phase-duration">{phase.duration}</span>
                </div>
              </div>

              <div className="phase-card-center">
                <h3 className="phase-card-title">{phase.title}</h3>
                <p className="phase-card-desc">{phase.description}</p>
                <div className="phase-activities">
                  {phase.activities.map((activity, i) => (
                    <span key={i} className="activity-tag">
                      <FaCheck /> {activity}
                    </span>
                  ))}
                </div>
              </div>

              <div className="phase-card-right">
                <div className="phase-progress">
                  <div className="phase-progress-bar" style={{ width: `${(phase.id / 6) * 100}%` }}></div>
                </div>
                <div className="phase-next">
                  {phase.id < 6 && (
                    <>
                      <span>Next: {phases[phase.id].title}</span>
                      <FaArrowRight />
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Process timeline */}
        <div className="process-timeline">
          {phases.map((phase, i) => (
            <motion.div
              key={phase.id}
              className="timeline-node"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              onClick={() => setActivePhase(phase.id)}
            >
              <div 
                className="timeline-dot"
                style={{ 
                  background: activePhase === phase.id ? phase.color : '#ddd',
                  borderColor: activePhase === phase.id ? phase.color : 'transparent'
                }}
              />
              <span className="timeline-label">{phase.title}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ═══ PRINCIPLES - Card grid ════════════════════════ */}
      <section className="principles-section">
        <div className="principles-header">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="principles-tag"
          >
            <span>WHY IT WORKS</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Four principles.<br />
            <span>Zero exceptions.</span>
          </motion.h2>
        </div>

        <div className="principles-grid">
          {principles.map((principle, i) => (
            <motion.div
              key={i}
              className="principle-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              whileHover={{ y: -8 }}
            >
              <div className="principle-icon">
                <principle.icon />
              </div>
              <h3>{principle.title}</h3>
              <p>{principle.desc}</p>
              <div className="principle-number">0{i + 1}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ═══ DEEP DIVE - Accordion style ═══════════════════ */}
      <section className="deepdive-section">
        <div className="deepdive-container">
          <motion.div
            className="deepdive-left"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="deepdive-tag">DEEP DIVE</div>
            <h2>
              What happens<br />
              <span>inside each phase</span>
            </h2>
            <p>
              Click any phase to explore the detailed breakdown of activities,
              deliverables, and outcomes.
            </p>
            <div className="deepdive-stats">
              <div>
                <span>12+</span>
                <small>deliverables per phase</small>
              </div>
              <div>
                <span>100%</span>
                <small>client review points</small>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="deepdive-right"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {phases.map((phase, i) => (
              <motion.details
                key={phase.id}
                className="phase-details"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <summary>
                  <span className="summary-number">0{phase.id}</span>
                  <span className="summary-title">{phase.title}</span>
                  <span className="summary-duration">{phase.duration}</span>
                </summary>
                <div className="details-content">
                  <p>{phase.description}</p>
                  <div className="details-grid">
                    <div>
                      <strong>Activities</strong>
                      <ul>
                        {phase.activities.map((a, idx) => (
                          <li key={idx}>{a}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <strong>You receive</strong>
                      <ul>
                        <li>Phase completion report</li>
                        <li>Code/documentation</li>
                        <li>Review session</li>
                        <li>Next phase roadmap</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.details>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ CASE STUDY TEASER ═════════════════════════════ */}
      <section className="casestudy-teaser">
        <div className="teaser-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="teaser-tag"
          >
            REAL RESULTS
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            From <span>idea to successful </span> product
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
Many of our clients start with just an idea. With our experience, guidance, and technical expertise, we help turn those ideas into stable, scalable, and successful digital products built for long-term performance.          </motion.p>
          <motion.a
            href="#"
            className="teaser-link"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Read case study <FaArrowRight />
          </motion.a>
        </div>
        <div className="teaser-visual">
          <div className="teaser-graph">
            <div className="graph-bar" style={{ height: '40%' }}></div>
            <div className="graph-bar" style={{ height: '65%' }}></div>
            <div className="graph-bar" style={{ height: '85%' }}></div>
            <div className="graph-bar" style={{ height: '100%' }}></div>
            <div className="graph-bar" style={{ height: '70%' }}></div>
          </div>
        </div>
      </section>

      {/* ═══ CTA - Clean version ═══════════════════════════ */}
      <section className="process-cta" id="contact">
        <div className="cta-container">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to build with certainty?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Let's walk through the process together. No pitch, just a conversation about your vision.
          </motion.p>
          <motion.div
            className="cta-actions"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <a
              href="https://wa.me/916393561379?text=Hello%20I'm%20looking%20for%20software%20development%20services.%20Please%20share%20details."
              className="cta-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Start the conversation <FaArrowRight />
            </a>
            <a href="/" className="cta-secondary">
              Read FAQs
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
} 